import { useState, useEffect, useRef } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import StepIndicator from './components/StepIndicator';
import Step1EventType from './components/Step1EventType';
import Step2Participants from './components/Step2Participants';
import Step3DateRange from './components/Step3DateRange';
import Step4Contact from './components/Step4Contact';

interface FormData {
  // Step 1
  eventType: string;
  eventSubtype: string;
  // Step 2
  participants: string;
  channels: string;
  city: string;
  venue: string;
  // Step 3
  startDate: string;
  endDate: string;
  flexibility: string;
  duration: string;
  // Step 4
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

const INITIAL_DATA: FormData = {
  eventType: '',
  eventSubtype: '',
  participants: '',
  channels: '',
  city: '',
  venue: '',
  startDate: '',
  endDate: '',
  flexibility: '',
  duration: '',
  name: '',
  email: '',
  phone: '',
  company: '',
  message: '',
};

const STEPS = [
  { number: 1, label: 'Tipo de Evento', icon: 'ri-calendar-event-line' },
  { number: 2, label: 'Participantes', icon: 'ri-group-line' },
  { number: 3, label: 'Data', icon: 'ri-calendar-2-line' },
  { number: 4, label: 'Contato', icon: 'ri-user-line' },
];

const STEP_BG_IMAGES = [
  '/img/stib/tech-ia-traducao.webp',
  '/img/stib/congresso-coffee.webp',
  '/img/stib/congresso-palco-led.webp',
  '/img/stib/tech-rsi-remoto.webp',
];

export default function OrcamentoPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(INITIAL_DATA);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Solicitar Orçamento | Palestra Silenciosa | STIB';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Solicite seu orçamento para palestra silenciosa e fones sem fio. Formulário rápido em 4 passos — tipo de evento, participantes, data e contato. Resposta em até 24 horas.'
      );
    }

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) lastModified.setAttribute('content', '2026-05-18');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Solicitar Orçamento — STIB Palestra Silenciosa',
      description: 'Solicite seu orçamento para palestra silenciosa e fones sem fio. Formulário rápido em 4 passos. Resposta em até 24 horas.',
      url: `${siteUrl}/orcamento`,
      inLanguage: 'pt-BR',
      isPartOf: { '@id': `${siteUrl}/#website` },
      dateModified: '2026-05-18',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Solicitar Orçamento', item: `${siteUrl}/orcamento` },
        ],
      },
    };

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Orçamento de Palestra Silenciosa',
      description: 'Solicite um orçamento personalizado para palestra silenciosa, aluguel de fones sem fio e tecnologia de áudio para eventos corporativos. Atendemos em todo o Brasil.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'STIB — Palestra Silenciosa Brasil',
        telephone: '+55-11-91777-6227',
        url: siteUrl,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'São Paulo',
          addressRegion: 'SP',
          addressCountry: 'BR',
        },
      },
      areaServed: { '@type': 'Country', name: 'Brasil' },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'BRL',
        description: 'Orçamento 100% gratuito e sem compromisso. Resposta em até 24 horas.',
      },
    };

    const ids = ['orcamento-webpage-schema', 'orcamento-service-schema'];
    ids.forEach((id) => { document.getElementById(id)?.remove(); });

    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.id = 'orcamento-webpage-schema';
    s1.text = JSON.stringify(webPageSchema);
    document.head.appendChild(s1);

    const s2 = document.createElement('script');
    s2.type = 'application/ld+json';
    s2.id = 'orcamento-service-schema';
    s2.text = JSON.stringify(serviceSchema);
    document.head.appendChild(s2);

    return () => {
      ids.forEach((id) => { document.getElementById(id)?.remove(); });
    };
  }, []);

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleChange = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const goNext = () => {
    scrollToTop();
    setTimeout(() => setCurrentStep((s) => Math.min(s + 1, 4)), 100);
  };

  const goBack = () => {
    scrollToTop();
    setTimeout(() => setCurrentStep((s) => Math.max(s - 1, 1)), 100);
  };

  const bgImage = STEP_BG_IMAGES[currentStep - 1];

  return (
    <>
      <Header />

      {/* Hero strip */}
      <section className="relative pt-20 pb-0 overflow-hidden">
        <div className="absolute inset-0 h-72">
          <img
            src="/img/stib/festa-silent-disco.webp"
            alt="Solicitar Orçamento Palestra Silenciosa"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-secondary/80 via-secondary/60 to-white"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
          <span className="inline-block font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 bg-white/10 backdrop-blur-sm border border-white/20 px-5 py-2 rounded-full text-white/90">
            Orçamento Gratuito · Resposta em até 24 horas Úteis
          </span>
          <h1 className="font-heading font-extrabold text-5xl md:text-6xl text-white mb-4 leading-tight">
            Solicitar Orçamento
          </h1>
          <p className="font-body text-lg text-white/80 max-w-xl mx-auto">
            Preencha o formulário em 4 passos simples e receba uma proposta personalizada para o seu evento.
          </p>
        </div>
      </section>

      {/* Main form area */}
      <main className="bg-white py-16 md:py-24 -mt-4">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

            {/* Form card */}
            <div ref={containerRef} className="lg:col-span-3">
              <div className="bg-white rounded-3xl border border-gray-100 p-8 md:p-12" style={{ boxShadow: '0 4px 32px rgba(56,71,116,0.07)' }}>
                <StepIndicator steps={STEPS} currentStep={currentStep} />

                {/* Animated step content */}
                <div
                  key={currentStep}
                  className="transition-all duration-300"
                  style={{ animation: 'fadeSlideUp 0.35s cubic-bezier(0.22,0.68,0,1.2) both' }}
                >
                  {currentStep === 1 && (
                    <Step1EventType
                      data={{ eventType: formData.eventType, eventSubtype: formData.eventSubtype }}
                      onChange={handleChange}
                      onNext={goNext}
                    />
                  )}
                  {currentStep === 2 && (
                    <Step2Participants
                      data={{
                        participants: formData.participants,
                        channels: formData.channels,
                        city: formData.city,
                        venue: formData.venue,
                      }}
                      onChange={handleChange}
                      onNext={goNext}
                      onBack={goBack}
                    />
                  )}
                  {currentStep === 3 && (
                    <Step3DateRange
                      data={{
                        startDate: formData.startDate,
                        endDate: formData.endDate,
                        flexibility: formData.flexibility,
                        duration: formData.duration,
                      }}
                      onChange={handleChange}
                      onNext={goNext}
                      onBack={goBack}
                    />
                  )}
                  {currentStep === 4 && (
                    <Step4Contact
                      data={formData}
                      onChange={handleChange}
                      onBack={goBack}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-2 space-y-6 lg:sticky lg:top-28">
              {/* Step image */}
              <div className="rounded-3xl overflow-hidden h-64 lg:h-72">
                <img
                  key={bgImage}
                  src={bgImage}
                  alt={`Passo ${currentStep} — ${STEPS[currentStep - 1].label}`}
                  className="w-full h-full object-cover object-top transition-opacity duration-500"
                  style={{ animation: 'fadeIn 0.5s ease both' }}
                />
              </div>

              {/* Why us */}
              <div className="bg-secondary rounded-3xl p-8 text-white">
                <p className="font-body text-xs font-bold uppercase tracking-widest text-primary mb-5">
                  Por que a STIB?
                </p>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-timer-flash-line', text: 'Orçamento em até 24 horas' },
                    { icon: 'ri-shield-check-line', text: 'NF-e e contrato formal incluídos' },
                    { icon: 'ri-headphone-line', text: 'Equipamentos certificados Anatel' },
                    { icon: 'ri-customer-service-2-line', text: 'Técnico presente no seu evento' },
                    { icon: 'ri-map-pin-line', text: 'Atendemos em todo o Brasil' },
                    { icon: 'ri-star-fill', text: '+500 eventos realizados, 98% satisfação' },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-primary/20 rounded-full flex-shrink-0">
                        <i className={`${item.icon} text-primary text-sm`}></i>
                      </div>
                      <p className="font-body text-sm text-white/85">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* WhatsApp CTA */}
              <a
                href="https://wa.me/5511917776227?text=Olá! Gostaria de solicitar um orçamento para palestra silenciosa."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-5 bg-green-50 border border-green-200 rounded-2xl hover:bg-green-100 transition-colors cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0">
                  <i className="ri-whatsapp-line text-white text-2xl"></i>
                </div>
                <div>
                  <p className="font-body font-bold text-green-800 text-sm">Prefere pelo WhatsApp?</p>
                  <p className="font-body text-xs text-green-600">Fale diretamente com nossa equipe agora</p>
                </div>
                <div className="w-6 h-6 flex items-center justify-center ml-auto">
                  <i className="ri-arrow-right-line text-green-600 group-hover:translate-x-1 transition-transform"></i>
                </div>
              </a>
            </aside>
          </div>
        </div>
      </main>

      {/* Subtle FAQ strip */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading font-bold text-2xl text-secondary text-center mb-8">
            Dúvidas sobre o orçamento?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                q: 'Quanto tempo demora?',
                a: 'Nossa equipe retorna em até 24 horas com uma proposta detalhada e personalizada.',
                icon: 'ri-timer-line',
              },
              {
                q: 'O orçamento tem algum custo?',
                a: 'Não. O orçamento é 100% gratuito e sem compromisso de contratação.',
                icon: 'ri-money-cny-circle-line',
              },
              {
                q: 'E se a data mudar?',
                a: 'Sem problema! Você pode atualizar a proposta a qualquer momento antes do evento.',
                icon: 'ri-calendar-check-line',
              },
            ].map((item) => (
              <div key={item.q} className="text-center p-6 bg-white rounded-2xl border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                  <i className={`${item.icon} text-primary text-xl`}></i>
                </div>
                <h3 className="font-body font-bold text-secondary text-sm mb-2">{item.q}</h3>
                <p className="font-body text-xs text-gray-500 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </>
  );
}